/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — Toaster
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * A self-contained toast notification system with:
 * - Multiple toast types (success, error, warning, info)
 * - Auto-dismiss with configurable duration
 * - Stacking with smooth animations
 * - Portal rendering (always on top)
 * - Action buttons support
 * - Progress bar countdown
 * 
 * Usage:
 *   import { Toaster, useToast } from '@/core/oblique-ui/Toaster'
 * 
 *   // In your root layout:
 *   <Toaster />
 * 
 *   // In any component:
 *   const { toast } = useToast();
 *   toast.success('Deal saved successfully');
 *   toast.error('Failed to save', { duration: 5000 });
 *   toast.info('Processing...', { action: { label: 'Undo', onClick: () => {} } });
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';


/* ═══════════════════════════════════════════════════════════════════════════
 * Constants
 * ═══════════════════════════════════════════════════════════════════════════ */

let toastId = 0;
const generateToastId = () => `toast-${++toastId}`;

const TOAST_CONFIG = {
    success: { icon: 'check_circle', color: '#22c55e', bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-800' },
    error:   { icon: 'error',        color: '#ef4444', bg: 'bg-red-50 dark:bg-red-950/30',         border: 'border-red-200 dark:border-red-800' },
    warning: { icon: 'warning',      color: '#f59e0b', bg: 'bg-amber-50 dark:bg-amber-950/30',     border: 'border-amber-200 dark:border-amber-800' },
    info:    { icon: 'info',         color: '#3b82f6', bg: 'bg-blue-50 dark:bg-blue-950/30',       border: 'border-blue-200 dark:border-blue-800' },
    default: { icon: 'notifications',color: 'var(--text-secondary)', bg: 'bg-[var(--bg-surface)]', border: 'border-[var(--border)]' },
};

const TOAST_ANIMATION = {
    initial: { opacity: 0, y: 16, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, x: 80, scale: 0.95 },
};
const TOAST_TRANSITION = { type: 'spring', stiffness: 400, damping: 30, mass: 0.8 };

const DEFAULT_DURATION = 4000;


/* ═══════════════════════════════════════════════════════════════════════════
 * Toast Context — global state
 * ═══════════════════════════════════════════════════════════════════════════ */

const ToastContext = createContext(null);

// Global listeners for imperative usage outside React tree
let globalListeners = [];
const addGlobalListener = (fn) => { globalListeners.push(fn); return () => { globalListeners = globalListeners.filter(l => l !== fn); }; };
const notifyGlobal = (toast) => { globalListeners.forEach(fn => fn(toast)); };

// Imperative toast API (works anywhere, even outside React components)
export const toast = {
    success: (message, opts = {}) => notifyGlobal({ type: 'success', message, ...opts }),
    error:   (message, opts = {}) => notifyGlobal({ type: 'error',   message, ...opts }),
    warning: (message, opts = {}) => notifyGlobal({ type: 'warning', message, ...opts }),
    info:    (message, opts = {}) => notifyGlobal({ type: 'info',    message, ...opts }),
    custom:  (message, opts = {}) => notifyGlobal({ type: 'default', message, ...opts }),
    dismiss: (id) => notifyGlobal({ __dismiss: id }),
};


/* ═══════════════════════════════════════════════════════════════════════════
 * useToast — hook for components
 * ═══════════════════════════════════════════════════════════════════════════ */

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (ctx) return ctx;
    // Fallback to imperative API
    return { toast };
};


/* ═══════════════════════════════════════════════════════════════════════════
 * Single Toast Item
 * ═══════════════════════════════════════════════════════════════════════════ */

const ToastItem = ({ toast: t, onDismiss }) => {
    const config = TOAST_CONFIG[t.type] || TOAST_CONFIG.default;
    const [progress, setProgress] = useState(100);
    const [isPaused, setIsPaused] = useState(false);
    const timerRef = useRef(null);
    const startTimeRef = useRef(Date.now());
    const remainingRef = useRef(t.duration || DEFAULT_DURATION);

    useEffect(() => {
        const startTimer = () => {
            const remaining = remainingRef.current;
            const interval = 50;
            timerRef.current = setInterval(() => {
                const elapsed = Date.now() - startTimeRef.current;
                const pct = Math.max(0, 100 - (elapsed / (t.duration || DEFAULT_DURATION)) * 100);
                setProgress(pct);
                if (pct <= 0) {
                    clearInterval(timerRef.current);
                    onDismiss(t.id);
                }
            }, interval);
        };

        if (!isPaused) {
            startTimeRef.current = Date.now() - ((t.duration || DEFAULT_DURATION) - remainingRef.current);
            startTimer();
        }

        return () => clearInterval(timerRef.current);
    }, [isPaused]);

    const handlePause = () => {
        setIsPaused(true);
        clearInterval(timerRef.current);
        remainingRef.current = remainingRef.current - (Date.now() - startTimeRef.current);
    };

    const handleResume = () => {
        setIsPaused(false);
    };

    return (
        <motion.div
            layout
            variants={TOAST_ANIMATION}
            initial="initial" animate="animate" exit="exit"
            transition={TOAST_TRANSITION}
            onMouseEnter={handlePause}
            onMouseLeave={handleResume}
            className={`relative flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-sm min-w-[320px] max-w-[420px] cursor-default overflow-hidden ${config.bg} ${config.border}`}
        >
            {/* Icon */}
            <div className="shrink-0 mt-0.5">
                <ObliqueIcon name={config.icon} size={20} color={config.color} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                {t.title && <div className="text-[13px] font-semibold text-[var(--text-primary)] mb-0.5">{t.title}</div>}
                <div className="text-[13px] text-[var(--text-secondary)] leading-relaxed">{t.message}</div>
                {t.action && (
                    <button onClick={() => { t.action.onClick?.(); onDismiss(t.id); }}
                        className="mt-2 text-xs font-medium px-2.5 py-1 rounded-md bg-[var(--bg-muted)] text-[var(--text-primary)] hover:bg-[var(--bg-subtle)] transition-colors border border-[var(--border)] cursor-pointer">
                        {t.action.label}
                    </button>
                )}
            </div>

            {/* Dismiss */}
            <button onClick={() => onDismiss(t.id)}
                className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center bg-transparent border-none text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] cursor-pointer transition-colors">
                <ObliqueIcon name="close" size={16} />
            </button>

            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-black/5 dark:bg-white/5">
                <motion.div
                    className="h-full rounded-full"
                    style={{ width: `${progress}%`, backgroundColor: config.color, opacity: 0.6 }}
                    transition={{ duration: 0.05 }}
                />
            </div>
        </motion.div>
    );
};


/* ═══════════════════════════════════════════════════════════════════════════
 * Toaster — Renders all active toasts
 * ═══════════════════════════════════════════════════════════════════════════ */

export const Toaster = ({ position = 'bottom-right' }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((data) => {
        if (data.__dismiss) {
            setToasts(prev => prev.filter(t => t.id !== data.__dismiss));
            return;
        }
        const newToast = {
            id: generateToastId(),
            type: data.type || 'default',
            message: data.message,
            title: data.title,
            duration: data.duration || DEFAULT_DURATION,
            action: data.action,
        };
        setToasts(prev => [...prev, newToast].slice(-5)); // max 5 toasts
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Listen to global imperative calls
    useEffect(() => {
        return addGlobalListener(addToast);
    }, [addToast]);

    const positionClasses = {
        'top-right':    'top-4 right-4',
        'top-left':     'top-4 left-4',
        'top-center':   'top-4 left-1/2 -translate-x-1/2',
        'bottom-right': 'bottom-4 right-4',
        'bottom-left':  'bottom-4 left-4',
        'bottom-center':'bottom-4 left-1/2 -translate-x-1/2',
    }[position] || 'bottom-4 right-4';

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className={`fixed ${positionClasses} z-[9999] flex flex-col gap-2 pointer-events-none`}>
            <AnimatePresence mode="popLayout">
                {toasts.map(t => (
                    <div key={t.id} className="pointer-events-auto">
                        <ToastItem toast={t} onDismiss={dismissToast} />
                    </div>
                ))}
            </AnimatePresence>
        </div>,
        document.body
    );
};

export default Toaster;
